import type { CustomerDeliveryStateCode } from './customer-state'
import { classifyCdekStatus } from './cdek-status-vocabulary.generated'
import type { YMarketOrder } from './types'
import { orderShipment, paymentTotal } from './order-helpers'
import { isMarketReturnInProgress } from './order-returns'

export type MissionControlStage =
  | 'delivery_issue'
  | 'returning'
  | 'out_for_delivery'
  | 'ready_for_pickup'
  | 'in_transit'
  | 'preparing'
  | 'processing'
  | 'delivery_pending'
  | 'awaiting_payment'

export interface MissionControlOrder {
  order: YMarketOrder
  stage: MissionControlStage
  amount: number | null
  changedAt?: string
  plannedDeliveryDate?: string
  /** The server's Russian label for the selected CDEK leg or Yandex return fact. */
  statusName?: string | null
  /** Selected CDEK waybill; null when this row comes from a Yandex return fact. */
  cdekNumber?: string | null
  recipientProximity?: number
  handoffAccepted?: boolean
}

export interface MissionControlGroup {
  stage: MissionControlStage
  count: number
  knownAmount: number
  unknownAmountCount: number
  orders: MissionControlOrder[]
}

export interface MissionControlSummary {
  buyerBound: MissionControlFlowSummary
  returning: MissionControlFlowSummary
  totalInMotion: MissionControlFlowSummary
  groups: Record<MissionControlStage, MissionControlGroup>
  inTransit: MissionControlGroup
  potential: MissionControlGroup
  urgent: MissionControlSelection
}

export interface MissionControlSelection extends MissionControlFlowSummary {
  orders: MissionControlOrder[]
}

export interface AuthoritativeMissionControlSummary extends MissionControlSummary {
  generatedAt: string
  complete: true
  awaitingHandoff: MissionControlSelection
  stranded: MissionControlStrandedSummary
}

export interface MissionControlFlowSummary {
  count: number
  knownAmount: number
  unknownAmountCount: number
}

export interface MissionControlStrandedSummary {
  parcelCount: number
  pieceCount: number
  piecesUnknownCount: number
  expiredParcelCount: number
  knownAmount: number
  unknownAmountCount: number
}

export const MISSION_STAGE_ORDER: MissionControlStage[] = [
  'delivery_issue',
  'returning',
  'out_for_delivery',
  'ready_for_pickup',
  'in_transit',
  'preparing',
  'processing',
  'delivery_pending',
  'awaiting_payment',
]

export const MISSION_STAGE_META: Record<
  MissionControlStage,
  { label: string; shortLabel: string; description: string }
> = {
  delivery_issue: {
    label: 'Требуют внимания',
    shortLabel: 'Проблемы',
    description: 'СДЭК сообщил о проблеме доставки',
  },
  returning: {
    label: 'Возвращаются на наш склад',
    shortLabel: 'Возврат',
    description: 'СДЭК везёт отдельную возвратную накладную или Маркет сообщает о возврате в пути',
  },
  out_for_delivery: {
    label: 'У курьера',
    shortLabel: 'У курьера',
    description: 'Курьер уже везёт заказ покупателю',
  },
  ready_for_pickup: {
    label: 'Ждут получения',
    shortLabel: 'Ждут',
    description: 'Заказы находятся в пункте выдачи',
  },
  in_transit: {
    label: 'В пути',
    shortLabel: 'В пути',
    description: 'Заказы движутся между городами и складами',
  },
  preparing: {
    label: 'Готовятся к отправке',
    shortLabel: 'Готовятся',
    description: 'Накладная создана, заказ ещё не отправлен',
  },
  processing: {
    label: 'В обработке',
    shortLabel: 'Обработка',
    description: 'Заказы Яндекс Маркета ещё обрабатываются',
  },
  delivery_pending: {
    label: 'Доставляется',
    shortLabel: 'Доставляется',
    description: 'Яндекс доставляет заказ, точный этап пока неизвестен',
  },
  awaiting_payment: {
    label: 'Ожидают оплаты',
    shortLabel: 'Ждут оплаты',
    description: 'Созданные заказы Маркета, которые покупатель ещё не оплатил',
  },
}

const ACTIVE_SHIPMENT_STAGES = new Set<CustomerDeliveryStateCode>([
  'preparing',
  'in_transit',
  'ready_for_pickup',
  'out_for_delivery',
  'delivery_issue',
])

/** True only for a Market cancellation after handoff that is still unresolved. */
export function isReturningToSender(order: YMarketOrder): boolean {
  return isMarketReturnInProgress(order)
}

const PRIORITY: Record<MissionControlStage, number> = Object.fromEntries(
  MISSION_STAGE_ORDER.map((stage, index) => [stage, MISSION_STAGE_ORDER.length - index])
) as Record<MissionControlStage, number>

function stageForOrder(order: YMarketOrder): MissionControlStage | null {
  const shipment = orderShipment(order)
  // The projection already carries its closed state, decided by the one
  // server-side classifier. Re-reading the raw code here was a second policy
  // over the same fact and is deliberately gone.
  const shipmentState = shipment?.state
  if (isReturningToSender(order)) return 'returning'
  if (order.status === 'CANCELLED') return null
  if (shipmentState === 'returned') return null
  if (shipment?.problemCode) return 'delivery_issue'
  if (shipmentState && ACTIVE_SHIPMENT_STAGES.has(shipmentState)) {
    return shipmentState as MissionControlStage
  }
  if (order.status === 'PROCESSING') return 'processing'
  if (order.status === 'DELIVERY') return 'delivery_pending'
  if (order.status === 'PICKUP') return 'ready_for_pickup'
  if (order.status === 'UNPAID') return 'awaiting_payment'
  return null
}

export function safeAmount(order: YMarketOrder): number | null {
  const amount = paymentTotal(order)
  return amount !== null && Number.isFinite(amount) && amount >= 0 ? amount : null
}

export function isCompleteMissionControlSummary(
  value: unknown,
): value is AuthoritativeMissionControlSummary {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<AuthoritativeMissionControlSummary>
  const flowIsNumeric = (flow: MissionControlFlowSummary | undefined): boolean => Boolean(
    flow
    && Number.isInteger(flow.count) && flow.count >= 0
    && typeof flow.knownAmount === 'number' && Number.isFinite(flow.knownAmount) && flow.knownAmount >= 0
    && Number.isInteger(flow.unknownAmountCount) && flow.unknownAmountCount >= 0,
  )
  const itemHasNumericOrUnavailableAmount = (item: MissionControlOrder): boolean => (
    (typeof item.amount === 'number' && Number.isFinite(item.amount) && item.amount >= 0)
    || item.amount === null
  )
  const groups = candidate.groups
  const stranded = candidate.stranded
  return candidate.complete === true
    && typeof candidate.generatedAt === 'string'
    && Number.isFinite(Date.parse(candidate.generatedAt))
    && flowIsNumeric(candidate.buyerBound)
    && flowIsNumeric(candidate.returning)
    && flowIsNumeric(candidate.totalInMotion)
    && flowIsNumeric(candidate.awaitingHandoff)
    && stranded !== undefined
    && Number.isInteger(stranded.parcelCount) && stranded.parcelCount >= 0
    && Number.isInteger(stranded.pieceCount) && stranded.pieceCount >= 0
    && Number.isInteger(stranded.piecesUnknownCount) && stranded.piecesUnknownCount >= 0
    && Number.isInteger(stranded.expiredParcelCount) && stranded.expiredParcelCount >= 0
    && typeof stranded.knownAmount === 'number' && Number.isFinite(stranded.knownAmount) && stranded.knownAmount >= 0
    && Number.isInteger(stranded.unknownAmountCount) && stranded.unknownAmountCount >= 0
    && Array.isArray(candidate.awaitingHandoff?.orders)
    && candidate.awaitingHandoff.orders.every(itemHasNumericOrUnavailableAmount)
    && Boolean(groups)
    && MISSION_STAGE_ORDER.every((stage) => (
      flowIsNumeric(groups?.[stage])
      && Array.isArray(groups?.[stage].orders)
      && groups[stage].orders.every(itemHasNumericOrUnavailableAmount)
    ))
    && flowIsNumeric(candidate.urgent)
    && Array.isArray(candidate.urgent?.orders)
    && candidate.urgent.count === candidate.urgent.orders.length
    && candidate.urgent.orders.every(itemHasNumericOrUnavailableAmount)
}

function timestamp(value?: string): number {
  if (!value) return 0
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const STAGE_RECIPIENT_PROXIMITY: Record<MissionControlStage, number> = {
  delivery_issue: 91,
  returning: 0,
  out_for_delivery: 100,
  ready_for_pickup: 96,
  in_transit: 55,
  delivery_pending: 50,
  preparing: 30,
  processing: 10,
  awaiting_payment: 0,
}

/**
 * Higher values mean the shipment is physically closer to its recipient.
 *
 * The server publishes `recipientProximity` on every Mission Control item and
 * that value wins. The generated vocabulary is the fallback for a locally
 * built summary; a reviewed code that carries no proximity, and any code this
 * application has not reviewed, both fall back to the stage.
 */
export function missionRecipientProximity(item: MissionControlOrder): number {
  if (typeof item.recipientProximity === 'number') return item.recipientProximity
  const proximity = classifyCdekStatus(orderShipment(item.order)?.statusCode)?.proximity
  return proximity ?? STAGE_RECIPIENT_PROXIMITY[item.stage]
}

function emptyGroup(stage: MissionControlStage): MissionControlGroup {
  return { stage, count: 0, knownAmount: 0, unknownAmountCount: 0, orders: [] }
}

export function buildMissionControlSummary(orders: YMarketOrder[]): MissionControlSummary {
  const groups = Object.fromEntries(
    MISSION_STAGE_ORDER.map((stage) => [stage, emptyGroup(stage)])
  ) as Record<MissionControlStage, MissionControlGroup>

  for (const order of orders) {
    const stage = stageForOrder(order)
    if (!stage) continue
    const shipment = orderShipment(order)
    const item: MissionControlOrder = {
      order,
      stage,
      amount: safeAmount(order),
      changedAt: shipment?.changedAt || order.updateDate || order.creationDate,
      // Only CDEK's tracking plan is a confirmed delivery-plan signal. Market
      // delivery dates are not shown as a CDEK plan in Mission Control.
      plannedDeliveryDate: shipment?.plannedDeliveryDate,
      statusName: shipment?.statusName,
      cdekNumber: shipment?.cdekNumber,
      handoffAccepted: shipment?.handoffAccepted,
    }
    const group = groups[stage]
    group.orders.push(item)
    group.count += 1
    if (item.amount === null) group.unknownAmountCount += 1
    else group.knownAmount += item.amount
  }

  for (const group of Object.values(groups)) {
    group.orders.sort((left, right) => {
      const leftPlan = left.plannedDeliveryDate ? timestamp(left.plannedDeliveryDate) : Number.POSITIVE_INFINITY
      const rightPlan = right.plannedDeliveryDate ? timestamp(right.plannedDeliveryDate) : Number.POSITIVE_INFINITY
      return leftPlan - rightPlan || timestamp(right.changedAt) - timestamp(left.changedAt)
    })
  }

  const active = Object.values(groups)
    .filter((group) => group.stage !== 'awaiting_payment')
    .flatMap((group) => group.orders)
  const returning = groups.returning.orders
  const buyerBound = active.filter((item) => item.stage !== 'returning')
  const flowSummary = (items: MissionControlOrder[]): MissionControlFlowSummary => ({
    count: items.length,
    knownAmount: items.reduce((sum, item) => sum + (item.amount ?? 0), 0),
    unknownAmountCount: items.filter((item) => item.amount === null).length,
  })
  const buyerBoundSummary = flowSummary(buyerBound)
  const returningSummary = flowSummary(returning)
  const totalInMotion = flowSummary(active)
  const urgentItems = [...active]
    .sort((left, right) => {
      const priority = PRIORITY[right.stage] - PRIORITY[left.stage]
      return priority || timestamp(right.changedAt) - timestamp(left.changedAt)
    })
    .filter((item) => item.stage === 'delivery_issue' || item.stage === 'out_for_delivery')
  const urgent: MissionControlSelection = {
    ...flowSummary(urgentItems),
    orders: urgentItems,
  }

  return {
    buyerBound: buyerBoundSummary,
    returning: returningSummary,
    totalInMotion,
    groups,
    inTransit: groups.in_transit,
    potential: groups.awaiting_payment,
    urgent,
  }
}
